import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AppContext';
import { Heart, Send, Trash2, MessageCircle, X, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthPrompt } from './AuthPromptModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Auth-gate copy specifically for comments — same modal pattern we use for
// the like button (iter 23), different message tuned to the action. Lives
// next to the components that use it so it stays close to its callers.
const COMMENT_AUTH_PROMPT = {
  title: 'Sign in to join the conversation',
  body: 'Your comments save under your profile and help creators see who\'s tuned in.',
  primaryButton: 'Sign in',
  secondaryButton: 'Create an account',
  primaryPath: '/login',
};

// Surface POST/DELETE errors to the user (silent failure was the original
// "comments don't work" bug — the input would empty itself on success but
// on failure the text would just sit there with no feedback). Pulls the
// most user-meaningful string out of the axios error shape.
const errorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (err?.code === 'ECONNABORTED' || err?.name === 'CanceledError') {
    return 'Server took too long to respond — please try again.';
  }
  if (err?.message && err.message !== 'Network Error') return err.message;
  if (err?.message === 'Network Error') return 'Network error — check your connection and try again.';
  return fallback;
};

// Hard timeout for comment POST/like/delete. The recurring "button greys
// but nothing happens" bug couldn't be reproduced server-side or in a
// fresh browser, which points at the request hanging on the user's device
// (extension blocking, corrupted state, mobile carrier proxy quirk). With
// no timeout, axios waits indefinitely → posting=true forever → grey
// button. 15 s is generous enough for slow mobile networks but short
// enough that the user can recover instead of being stuck.
const COMMENT_REQUEST_TIMEOUT_MS = 15000;

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
};

const CommentItem = ({ comment, onLike, onDelete, currentUserId, isAdmin }) => {
  const [liked, setLiked] = useState(comment.is_liked || false);
  const [likeCount, setLikeCount] = useState(comment.likes || 0);

  const handleLike = async () => {
    const newLiked = !liked;
    setLiked(newLiked);
    setLikeCount(prev => newLiked ? prev + 1 : Math.max(0, prev - 1));
    if (onLike) onLike(comment.id);
  };

  const canDelete = currentUserId === comment.user_id || isAdmin;

  return (
    <div className="flex gap-3 py-3 group" data-testid={`comment-${comment.id}`}>
      <div className="w-8 h-8 rounded-full bg-zinc-800 flex-shrink-0 overflow-hidden">
        {comment.avatar ? (
          <img src={`${API}/api/files/${comment.avatar}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs font-bold text-zinc-400">
            {(comment.display_name || comment.username || '?').charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white">{comment.display_name || comment.username}</span>
          <span className="text-xs text-zinc-500">{timeAgo(comment.created_at)}</span>
        </div>
        <p className="text-sm text-zinc-300 mt-0.5 break-words">{comment.text}</p>
        <div className="flex items-center gap-4 mt-1.5">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1 text-xs transition-colors ${liked ? 'text-red-400' : 'text-zinc-500 hover:text-white'}`}
            data-testid={`comment-like-${comment.id}`}
          >
            <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-red-400' : ''}`} />
            {likeCount > 0 && <span>{likeCount}</span>}
          </button>
          {canDelete && (
            <button
              onClick={() => onDelete(comment.id)}
              className="text-xs text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
              data-testid={`comment-delete-${comment.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Inline comment section for Video Detail page
export const CommentSection = ({ videoId }) => {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  // Inline error state so a failed comment shows visible feedback inside
  // the page (toast is easy to miss on mobile). Cleared when the user
  // edits the text again so they can retry cleanly.
  const [lastError, setLastError] = useState('');
  const { user, axiosInstance } = useAuth();
  const { promptAuth } = useAuthPrompt();
  const ADMIN_EMAILS = ["cpowersv@gmail.com", "modtest@aivue.com"];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);


  const fetchComments = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/videos/${videoId}/comments`);
      setComments(res.data.comments || []);
      setTotal(res.data.total || 0);
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, videoId]);

  useEffect(() => {
    if (videoId) fetchComments();
  }, [videoId, fetchComments]);

  const handlePost = async () => {
    if (!newComment.trim() || posting) return;
    setPosting(true);
    setLastError('');
    // Diagnostic log so a user with DevTools open can see the post is
    // actually being attempted (helps narrow recurring "nothing happens"
    // reports). Build/iteration string makes it obvious which bundle is
    // running on their device.
    // eslint-disable-next-line no-console
    console.log('[comments] POST start (iter97+)', { videoId, len: newComment.trim().length });
    try {
      const res = await axiosInstance.post(
        `/videos/${videoId}/comments`,
        { text: newComment.trim() },
        { timeout: COMMENT_REQUEST_TIMEOUT_MS }
      );
      setComments(prev => [res.data, ...prev]);
      setTotal(prev => prev + 1);
      setNewComment('');
      // eslint-disable-next-line no-console
      console.log('[comments] POST ok', { id: res.data?.id });
    } catch (err) {
      const status = err?.response?.status;
      // Special-case 401/403: the user's token was rejected, so a generic
      // "try again" toast wouldn't help — point them at re-login. This
      // covers the most plausible "comments don't work for me but work for
      // E1 from a fresh session" scenario.
      let msg;
      if (status === 401 || status === 403) {
        msg = 'Your sign-in expired. Please sign in again to leave a comment.';
      } else {
        msg = errorMessage(err, `Could not post comment (HTTP ${status || '?'}). Try again.`);
      }
      // Silent-fail was the original "comments don't work" bug — surface
      // failure to the user via toast AND inline message so they know to
      // retry or sign in again.
      // eslint-disable-next-line no-console
      console.error('[comments] POST failed', {
        status,
        detail: err?.response?.data?.detail,
        code: err?.code,
        name: err?.name,
        message: err?.message,
      });
      toast.error(msg);
      setLastError(msg);
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (commentId) => {
    try {
      await axiosInstance.post(`/comments/${commentId}/like`);
    } catch (err) {
      console.error('Failed to like comment:', err.message);
      toast.error(errorMessage(err, 'Could not like comment.'));
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axiosInstance.delete(`/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to delete comment:', err.message);
      toast.error(errorMessage(err, 'Could not delete comment.'));
    }
  };

  return (
    <div className="mt-6" data-testid="comment-section">
      <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
        <MessageCircle className="w-5 h-5" />
        Comments {total > 0 && <span className="text-zinc-500 text-sm font-normal">({total})</span>}
      </h3>

      {/* Comment input */}
      {user ? (
        <>
        <div className="flex gap-3 mb-4" data-testid="comment-input-area">
          <div className="w-8 h-8 rounded-full bg-zinc-800 flex-shrink-0 overflow-hidden">
            {user.avatar ? (
              <img src={`${API}/api/files/${user.avatar}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs font-bold text-zinc-400">
                {(user.display_name || user.username || '?').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => { setNewComment(e.target.value); if (lastError) setLastError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handlePost()}
              placeholder={posting ? 'Sending…' : 'Add a comment...'}
              disabled={posting}
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:opacity-60"
              data-testid="comment-input"
            />
            <button
              onClick={handlePost}
              disabled={!newComment.trim() || posting}
              className="w-9 h-9 rounded-full bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 disabled:text-zinc-500 flex items-center justify-center transition-colors"
              title={posting ? 'Sending…' : 'Post comment'}
              aria-label={posting ? 'Sending comment' : 'Post comment'}
              data-testid="comment-submit"
            >
              {posting
                ? <Loader2 className="w-4 h-4 text-white animate-spin" data-testid="comment-sending-spinner" />
                : <Send className="w-4 h-4 text-white" />}
            </button>
          </div>
        </div>
        {lastError && (
          <div
            className="flex items-start gap-2 mb-4 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs"
            role="alert"
            data-testid="comment-error-banner"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Couldn&apos;t post comment</div>
              <div className="opacity-90 mt-0.5 break-words">{lastError}</div>
              {lastError && lastError.toLowerCase().includes('sign-in expired') ? (
                <button
                  onClick={() => { window.location.href = '/login'; }}
                  className="mt-1 underline underline-offset-2 hover:text-red-200"
                  data-testid="comment-error-signin"
                >
                  Sign in again
                </button>
              ) : (
                <button
                  onClick={() => { setLastError(''); handlePost(); }}
                  className="mt-1 underline underline-offset-2 hover:text-red-200"
                  data-testid="comment-error-retry"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        )}
        </>
      ) : (
        <button
          onClick={() => promptAuth(COMMENT_AUTH_PROMPT)}
          className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-sm text-zinc-400 mb-4 transition-colors"
          data-testid="comment-signin-cta"
        >
          Sign in to leave a comment…
        </button>
      )}

      {/* Comments list */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={`skeleton-${i}`} className="flex gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-zinc-800" />
              <div className="flex-1">
                <div className="h-3 bg-zinc-800 rounded w-24 mb-2" />
                <div className="h-3 bg-zinc-800 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-6">No comments yet. Be the first!</p>
      ) : (
        <div className="divide-y divide-zinc-800/50">
          {comments.map(c => (
            <CommentItem
              key={c.id}
              comment={c}
              onLike={handleLike}
              onDelete={handleDelete}
              currentUserId={user?.id}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Modal comment panel for Pulse page
export const CommentModal = ({ videoId, isOpen, onClose }) => {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const { user, axiosInstance } = useAuth();
  const { promptAuth } = useAuthPrompt();
  const inputRef = useRef(null);
  const ADMIN_EMAILS = ["cpowersv@gmail.com", "modtest@aivue.com"];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);


  const fetchComments = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/videos/${videoId}/comments`);
      setComments(res.data.comments || []);
      setTotal(res.data.total || 0);
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, videoId]);

  useEffect(() => {
    if (isOpen && videoId) {
      fetchComments();
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, videoId, fetchComments]);

  const handlePost = async () => {
    if (!newComment.trim() || posting) return;
    setPosting(true);
    try {
      const res = await axiosInstance.post(
        `/videos/${videoId}/comments`,
        { text: newComment.trim() },
        { timeout: COMMENT_REQUEST_TIMEOUT_MS }
      );
      setComments(prev => [res.data, ...prev]);
      setTotal(prev => prev + 1);
      setNewComment('');
    } catch (err) {
      console.error('Failed to post comment:', err.message);
      toast.error(errorMessage(err, 'Could not post comment. Try again.'));
    }
    setPosting(false);
  };

  const handleLike = async (commentId) => {
    try {
      await axiosInstance.post(`/comments/${commentId}/like`);
    } catch (err) {
      console.error('Comment like failed:', err.message);
      toast.error(errorMessage(err, 'Could not like comment.'));
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axiosInstance.delete(`/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to delete comment:', err.message);
      toast.error(errorMessage(err, 'Could not delete comment.'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" onClick={onClose} data-testid="comment-modal">
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-lg bg-zinc-900 rounded-t-2xl max-h-[70vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <h3 className="text-white font-semibold">Comments {total > 0 && `(${total})`}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="close-comments">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Comments list */}
        <div className="flex-1 overflow-y-auto px-4 py-2" style={{ minHeight: '200px' }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-zinc-600 border-t-white rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No comments yet</p>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {comments.map(c => (
                <CommentItem
                  key={c.id}
                  comment={c}
                  onLike={handleLike}
                  onDelete={handleDelete}
                  currentUserId={user?.id}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        {user ? (
          <div className="flex gap-2 px-4 py-3 border-t border-zinc-800">
            <input
              ref={inputRef}
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePost()}
              placeholder="Add a comment..."
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
              data-testid="modal-comment-input"
            />
            <button
              onClick={handlePost}
              disabled={!newComment.trim() || posting}
              className="w-9 h-9 rounded-full bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 flex items-center justify-center"
              data-testid="modal-comment-submit"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        ) : (
          <div className="px-4 py-3 border-t border-zinc-800">
            <button
              onClick={() => { onClose(); promptAuth(COMMENT_AUTH_PROMPT); }}
              className="w-full bg-orange-500 hover:bg-orange-400 text-black font-semibold py-2.5 rounded-full text-sm transition-colors"
              data-testid="modal-comment-signin-cta"
            >
              Sign in to comment
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
